import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { ThemeContext } from "../../../context/ThemeContext";
import { useAuthContext } from "../../../hooks/contexts/useAuthContext";
import axios from "axios";
import { getColors } from "../../../themes/theme";
import { CarePlanEstimationItem } from "./CarePlanEstimationItem";
import { EstimationDialog } from "../EstimationDialog";
import { useSnackbar } from "notistack";

export const CarePlanEstimation = ({
  onCheckedItemsChange,
  checkedIDs,
  disabled,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { token, user } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [allEstimations, setAllEstimations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [checkedItems, setCheckedItems] = useState(new Set()); // Track checked item IDs

  useEffect(() => {
    if (checkedIDs !== null) {
      const newCheckedItems = new Set(checkedIDs);
      setCheckedItems(newCheckedItems);
    }
  }, [checkedIDs]); // Only run when checkedIDs changes

  useEffect(() => {
    // Notify the parent component whenever checkedItems changes
    const checkedEstimations = allEstimations.filter((estimation) =>
      checkedItems.has(estimation.id)
    );
    onCheckedItemsChange(checkedEstimations);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedItems, allEstimations]);

  const handleCheck = (id, isChecked) => {
    setCheckedItems((prev) => {
      const updated = new Set(prev);
      if (isChecked) {
        updated.add(id);
      } else {
        updated.delete(id);
      }
      return updated;
    });
  };

  const handleAddClicked = () => {
    setDialogData({ name: "", packages: [], payments: [] });
    setDialogOpen(true);
  };

  const handleSaveEstimation = async (data) => {
    try {
      if (data?.id) {
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/template/${data.id}`,
          {
            name: data.name,
            practiceid: user?.practiceId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const formattedPackages = data.packages.map((packageItem) => ({
          name: packageItem.name,
          visit: +packageItem.visit,
          cost: +packageItem.cost,
        }));

        const formattedPayments = data.payments?.map((packageItem) => ({
          payments: packageItem.payments,
          discount: +packageItem.discount,
          upfront: +packageItem.upfront,
        }));

        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepackage/bulkedit`,
          {
            templateid: response.data.id,
            packages: formattedPackages,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepayment/bulkedit`,
          {
            templateid: response.data.id,
            payments: formattedPayments,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        enqueueSnackbar("Successfully Changed", { variant: "success" });
        setAllEstimations((prev) =>
          prev
            .map((estimation) =>
              estimation.id === data.id ? response.data : estimation
            )
            .sort((a, b) => a.id > b.id)
        );
      } else {
        // Transform the packages array into the desired format

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/template`,
          {
            name: data.name,
            practiceid: user?.practiceId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const formattedPackages = data.packages.map((packageItem) => ({
          name: packageItem.name,
          visit: +packageItem.visit,
          cost: +packageItem.cost,
        }));

        const formattedPayments = data.payments?.map((packageItem) => ({
          payments: packageItem.payments,
          discount: +packageItem.discount,
          upfront: +packageItem.upfront,
        }));

        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepackage/bulkcreate`,
          {
            templateid: response.data.id,
            packages: formattedPackages,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepayment/bulkcreate`,
          {
            templateid: response.data.id,
            payments: formattedPayments,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        enqueueSnackbar("Successfully Added", { variant: "success" });
        setAllEstimations((prev) => {
          const updatedEstimations = [...prev, response.data];
          return updatedEstimations.sort((a, b) => a.id > b.id);
        });
      }
    } catch (err) {
      console.error("Error saving estimation:", err);
    }
  };

  useEffect(() => {
    if (!user || !token) return;
    const fetchAllEstimations = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/template/practiceid/${user?.practiceId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllEstimations(response.data);
      } catch (err) {
        console.error("Error fetching all estimations:", err);
      }
    };
    fetchAllEstimations();
  }, [user, token]);

  return (
    <Box display={disabled ? "none" : "block"}>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{
          xs: "column",
          lg: "row",
        }}
        sx={{ alignItems: "center" }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "28px",
            fontWeight: 500,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
            mr: "auto",
            mb: { xs: 1, sm: 0, md: 1, lg: 0 },
          }}
        >
          Good Faith Estimation
        </Typography>

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{
            fontFamily: "Poppins",
            fontSize: "18px",
            px: "20px",
            py: "12px",
            background: darkMode ? "#2D2E30" : "#E6E6E6",
            color: darkMode
              ? getColors.primaryTextDark
              : getColors.secondaryTextLight,
            ml: "auto",
            "&:hover": {
              color: getColors.primaryTextDark,
            },
          }}
          onClick={handleAddClicked}
        >
          New Template
        </Button>
      </Box>

      <Box
        display="grid"
        gap={4}
        sx={{
          mt: 5,
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(1, minmax(0, 1fr))",
            lg: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {allEstimations.map((estimation, index) => (
          <CarePlanEstimationItem
            key={index}
            {...estimation}
            onSave={handleSaveEstimation}
            onDelete={(id) =>
              setAllEstimations((prev) =>
                prev.filter((estimation) => estimation.id !== id)
              )
            }
            onCheck={handleCheck}
            isChecked={checkedItems.has(estimation.id)} // Pass checked status
          />
        ))}
      </Box>

      <EstimationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveEstimation}
        initialData={dialogData}
      />
    </Box>
  );
};
